import React, { useState, useMemo, useEffect, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ListLayout from '../../Generic/ListLayout';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useStartAttendance from '../../../hooks/useStartAttendance';
import useFetchPaginatedDataWithFilters from '../../../hooks/useFetchPaginatedDataWithFilters';
import DataTablePagination from '../../Generic/DataTablePagination';
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { createNewGeneralColumn, createDateColumn } from '../../Generic/dataGridColumns';
import { useStore } from '../../../stores/store';
import { useSetMenu } from "../../../hooks/useSetMenu";
import AuthContext from "../../../context/AuthProvider";

const SchedulePlanWeekListByPlanId = () => {
  const { mainStore } = useStore();
  const { auth } = useContext(AuthContext);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isReplacement = searchParams.get('isReplacement') === 'true';

  useSetMenu(
    isReplacement ? mainStore.ReplacementScheduleMenuType : mainStore.SchedulePlanMenuType,
    isReplacement ? mainStore.ReplacementScheduleMenuNo : mainStore.SchedulePlanMenuNo
  );

  const { t } = useTranslation();
  const { schedulePlanId } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const localizedTextsMap = useLocalizedTextsMap();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const endpoint = `/StaffPortalStaffs/GetSchedulePlanWeeksByPlanId/${schedulePlanId}`;

  const filterParams = useMemo(() => {
    let params = '';

    if (auth?.userId) {
      params += `&staffId=${auth.userId}`;

      if (isReplacement) {
        params += `&isReplacementOnly=true`;
      } else {
        params += `&isReplacementOnly=false`;
      }
    }

    return params;
  }, [isReplacement, auth?.userId]);

  // Asks which subjects the QR covers when the professor teaches others at the same time.
  const { startAttendance, attendanceDialog } = useStartAttendance();

  const { data: rawData, totalRecords } = useFetchPaginatedDataWithFilters(
    axiosPrivate,
    endpoint,
    page + 1,
    pageSize,
    searchQuery,
    filterParams
  );

  const [weeksWithSessions, setWeeksWithSessions] = useState({});
  const [activeSessionsInfo, setActiveSessionsInfo] = useState({});

  useEffect(() => {
    if (rawData && rawData.length > 0) {
      const weekIds = rawData.map(week => week.id);

      Promise.all(weekIds.map(weekId =>
        Promise.all([
          axiosPrivate.get(`/SchedulePlanWeekSessions/CheckSessionExists/${weekId}`)
            .catch(() => ({ data: { exists: false } })),
          axiosPrivate.get(`/SchedulePlanWeekSessions/ActiveSessions/${weekId}`)
            .catch(() => ({ data: { hasStartSession: false, hasEndSession: false } }))
        ])
      )).then(results => {
        const sessionMap = {};
        const activeMap = {};
        results.forEach((result, index) => {
          const weekId = weekIds[index];
          const [existsResult, activeResult] = result;
          sessionMap[weekId] = existsResult.data?.exists || false;
          activeMap[weekId] = {
            hasStartSession: activeResult.data?.hasStartSession || false,
            hasEndSession: activeResult.data?.hasEndSession || false,
            startSession: activeResult.data?.startSession || null,
            endSession: activeResult.data?.endSession || null,
            startSessionEverCreated: activeResult.data?.startSessionEverCreated || false,
            // The other subjects this lesson is combined with today: its QR opens for all of them.
            combinedWith: activeResult.data?.combinedWith || []
          };
        });
        setWeeksWithSessions(sessionMap);
        setActiveSessionsInfo(activeMap);
      });
    }
  }, [rawData, axiosPrivate]);
  
  const data = useMemo(() => {
    if (!rawData) return [];

    return rawData
      .map(item => ({
        ...item,
        hasSession: weeksWithSessions[item.id] || false,
        sessionInfo: activeSessionsInfo[item.id] || { hasStartSession: false, hasEndSession: false, startSessionEverCreated: false }
      }))
      .sort((a, b) => {
        const aTime = a.weekDay ? new Date(a.weekDay).getTime() : Number.MAX_SAFE_INTEGER;
        const bTime = b.weekDay ? new Date(b.weekDay).getTime() : Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
      });
  }, [rawData, weeksWithSessions, activeSessionsInfo]);

  const subjectName = rawData?.[0]?.subjectName || '';

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const pastSessions = data ? data.filter(session => new Date(session.weekDay) < now) : [];
  const lastPastSessionDate =
    pastSessions.length > 0
      ? new Date(Math.max(...pastSessions.map(session => new Date(session.weekDay).getTime())))
      : null;

  const viewAttendanceColumn = {
    field: 'viewAttendance',
    headerName: t("viewAttendance"),
    width: 150,
    headerAlign: "center",
    align: "center",
    headerClassName: 'tableHeaderClassName',
    cellClassName: 'tableCellClassName',
    renderHeader: (params) => (
      <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
  ),
    renderCell: (params) => {
      const hasAttendanceData = params.row.hasSession || false;
      
      const tooltipText = hasAttendanceData 
        ? t("viewAttendance") 
        : t("noSessionCreatedYet");
      
      return (
        <span 
          onClick={() => { 
            if (hasAttendanceData) {
              navigate(`/attendance/studentlist?schedulePlanWeekId=${params.row.id}`);
            }
          }}
          style={{
            padding: '5px 10px', 
            cursor: hasAttendanceData ? 'pointer' : 'not-allowed',
            opacity: hasAttendanceData ? 1 : 0.5
          }}
          className="text-brand"
          title={tooltipText}
        >
          {t("viewAttendance")}
        </span>
      );
    }
  };

  const columns = useMemo(() => [
    {
      field: 'attendance',
      headerName: t("attendance"),
      width: 230,
      headerAlign: "center",
      align: "center",
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderHeader: (params) => (
        <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
      ),
      renderCell: (params) => {
        const weekDayStr = params.row.weekDay;
        const scheduleDate = new Date(weekDayStr);
        const today = new Date();

        scheduleDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const isCurrentDate = scheduleDate.getTime() === today.getTime();
        const isPastDate = scheduleDate < today;
        const isFutureDate = scheduleDate > today;

        const isEnabled = isCurrentDate;

        const sessionInfo = params.row.sessionInfo || { hasStartSession: false, hasEndSession: false, startSessionEverCreated: false };
        const hasActiveStartSession = sessionInfo.hasStartSession;
        const hasActiveEndSession = sessionInfo.hasEndSession;
        const startSessionEverCreated = sessionInfo.startSessionEverCreated;

        const isEndEnabled = isEnabled && startSessionEverCreated;

        let startTooltip = "";
        let endTooltip = "";
        if (isPastDate) {
          startTooltip = t("cannotStartSessionForPastDate");
          endTooltip = t("cannotStartSessionForPastDate");
        } else if (isFutureDate) {
          startTooltip = t("cannotStartSessionForFutureDate");
          endTooltip = t("cannotStartSessionForFutureDate");
        } else {
          startTooltip = hasActiveStartSession ? t("startSessionActive") : t("startSessionBeginning");
          if (!startSessionEverCreated) {
            endTooltip = t("startSessionFirst");
          } else {
            endTooltip = hasActiveEndSession ? t("endSessionActive") : t("startSessionEnd");
          }
        }

        const combinedWith = sessionInfo.combinedWith || [];

        return (
          <div className="d-flex flex-column align-items-center py-1">
          <div className="d-flex gap-2 align-items-center">
            {}
            <button
              className={`btn btn-sm ${hasActiveStartSession ? 'btn-brand' : 'btn-outline-brand'}`}
              onClick={() => { if (isEnabled) startAttendance(params.row.id, 1); }}
              disabled={!isEnabled}
              title={startTooltip}
              style={{ minWidth: '70px' }}
            >
              <i className="bi bi-play-circle me-1"></i>
              {t("start")}
            </button>
            {}
            <button
              className={`btn btn-sm ${hasActiveEndSession ? 'btn-brand' : 'btn-outline-brand'}`}
              onClick={() => { if (isEndEnabled) startAttendance(params.row.id, 2); }}
              disabled={!isEndEnabled}
              title={endTooltip}
              style={{ minWidth: '70px' }}
            >
              <i className="bi bi-stop-circle me-1"></i>
              {t("end")}
            </button>
          </div>
          {combinedWith.length > 0 && (
            <span className="badge bg-danger mt-1" title={t("combinedQrLockedNote")}>
              <i className="bi bi-lock-fill me-1"></i>
              {t("combinedQrLockedWith", { subjects: combinedWith.join(", ") })}
            </span>
          )}
          </div>
        );
      }
    },
    viewAttendanceColumn,
    {
      field: 'weekDay',
      headerName: t("Week Day"),
      width: 150,
      headerAlign: "center",
      align: "center",
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderHeader: (params) => (
        <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
      ),
      renderCell: (params) => {
        const wd = params.row.weekDay ? new Date(params.row.weekDay) : null;
        return (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ lineHeight: 1.25, padding: '6px 0', gap: '2px', width: '100%' }}
          >
            <span style={{ fontWeight: 500 }}>
              {wd ? wd.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
            </span>
            {params.row.replacedSchedulePlanWeekId && params.row.replacedWeekDay && (
              <>
                <span
                  className="badge bg-info text-dark"
                  style={{ fontSize: '0.6rem', fontWeight: 500, padding: '2px 6px' }}
                  title={params.row.replacementReasonName || ''}
                >
                  ↺ {t("replaces") || "Replaces"} {new Date(params.row.replacedWeekDay).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </span>
                {params.row.replacementReasonName && (
                  <span
                    className="text-muted text-truncate"
                    style={{
                      fontSize: '0.6rem',
                      fontStyle: 'italic',
                      maxWidth: '170px',
                      lineHeight: 1.2
                    }}
                    title={params.row.replacementReasonName}
                  >
                    {params.row.replacementReasonName}
                  </span>
                )}
              </>
            )}
            {params.row.isReplaced && (
              <span className="badge bg-dark" style={{ fontSize: '0.6rem', fontWeight: 500, padding: '2px 6px' }}>
                {t("statusReplaced") || "Replaced"}
              </span>
            )}
          </div>
        );
      }
    },
    createNewGeneralColumn("scheduleTimeSlot", "Time Slot", t, 110),
    createNewGeneralColumn("branchObject", "Branch", t, 100),
    createNewGeneralColumn("room", "Room", t, 130),
    createNewGeneralColumn("groupPlanNames", "Groups", t, 100),
  ], [t, startAttendance, lastPastSessionDate, navigate]);

  return (
    <ListLayout
      title={subjectName ? `${t("Schedule Plan Weeks")} — ${subjectName}` : t("Schedule Plan Weeks")}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
    >
      <DataTablePagination
        rows={data}
        columns={columns}
        rowCount={totalRecords}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        localeTextsMap={localizedTextsMap}
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 60}
      />
      {attendanceDialog}
    </ListLayout>
  );
};

export default observer(SchedulePlanWeekListByPlanId);






          
  


  

  
  
  
  


